import { ref, reactive } from "vue";
import { useRoute } from "vue-router";
import mushroom from "cem-primary-api";
import MethodService from "../../../service/MethodService";
import toastr from "toastr";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import { useBreadcrumb } from "../../../stores/breadcrumb";

export function useDynamicConfig() {
  const { t } = useI18n();
  const breadcrumbStore = useBreadcrumb();
  const route = useRoute();
  const loading = ref(true);
  const pageName = ref("");
  const configTables = ref([]);
  const showEditDialog = ref(false);
  const editingRow = ref(null);
  const editingTable = ref(null);
  const editValues = ref([]);
  const editDialogTitle = ref("");
  const collapsedTables = reactive({});
  const sortState = reactive({});
  const searchKeyword = reactive({});
  const paginationState = reactive({});

  const getLang = () => localStorage.getItem("language") || "vi";

  const getDisplayName = (item) => {
    const lang = getLang();
    if (item.name_lang && item.name_lang[lang]) return item.name_lang[lang];
    return item.name || "";
  };

  const getColumnLabel = (col) => {
    const lang = getLang();
    if (col.label_lang && col.label_lang[lang]) return col.label_lang[lang];
    return col.label || col.code || "";
  };

  const getValueColumnName = (valCol) => {
    const lang = getLang();
    if (valCol.name_lang && valCol.name_lang[lang]) return valCol.name_lang[lang];
    return valCol.name || valCol.code || "";
  };

  const getIconUrl = (iconId) => {
    if (!iconId) return null;
    return MethodService.thumbIcon(iconId, 24);
  };

  const hasDefaultValue = (table) => {
    if (!table.value_columns) return false;
    return table.value_columns.some((vc) => vc.default_value_code);
  };

  const getRowId = (table, row) => {
    if (!table.id_column) return null;
    return row[table.id_column.code];
  };

  const toggleCollapse = (tableId) => {
    collapsedTables[tableId] = !collapsedTables[tableId];
  };

  const sortData = (data, colCode, direction) => {
    return [...data].sort((a, b) => {
      const valA = a[colCode];
      const valB = b[colCode];

      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      if (MethodService.isNumericValue(valA) && MethodService.isNumericValue(valB)) {
        const numA = Number(valA);
        const numB = Number(valB);
        return direction === "asc" ? numA - numB : numB - numA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) return direction === "asc" ? -1 : 1;
      if (strA > strB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getCurrentSort = (tableId) => {
    for (const key of Object.keys(sortState)) {
      if (key.startsWith(tableId + "_") && sortState[key]) {
        const colCode = key.substring(tableId.length + 1);
        return { colCode, direction: sortState[key] };
      }
    }
    return null;
  };

  const applyFilterAndSort = (table) => {
    const idx = configTables.value.findIndex((t) => t.id === table.id);
    if (idx === -1) return;

    const keyword = (searchKeyword[table.id] || "").trim().toLowerCase();
    let baseData;
    if (keyword) {
      const searchableCols = getSearchableColumns(table);
      baseData = table.originalData.filter((row) => {
        return searchableCols.some((col) => {
          const val = row[col.code];
          if (val == null) return false;
          return String(val).toLowerCase().includes(keyword);
        });
      });
    } else {
      baseData = [...table.originalData];
    }

    const currentSort = getCurrentSort(table.id);
    if (currentSort) {
      configTables.value[idx].tableData = sortData(baseData, currentSort.colCode, currentSort.direction);
    } else {
      configTables.value[idx].tableData = baseData;
    }

    if (paginationState[table.id]) {
      paginationState[table.id].page = 1;
    }
  };

  const handleSort = (table, { prop, order }) => {
    const idx = configTables.value.findIndex((t) => t.id === table.id);
    if (idx === -1) return;

    // Reset sort state cho table hiện tại
    Object.keys(sortState).forEach((k) => {
      if (k.startsWith(table.id + "_")) sortState[k] = null;
    });

    if (prop && order) {
      const direction = order === "ascending" ? "asc" : "desc";
      sortState[table.id + "_" + prop] = direction;
    }

    applyFilterAndSort(configTables.value[idx]);
  };

  const hasSearchableColumns = (table) => {
    if (!table.columns) return false;
    return table.columns.some((col) => col.allow_search);
  };

  const getSearchableColumns = (table) => {
    if (!table.columns) return [];
    return table.columns.filter((col) => col.allow_search);
  };

  const handleSearch = (table) => {
    const idx = configTables.value.findIndex((t) => t.id === table.id);
    if (idx === -1) return;
    applyFilterAndSort(configTables.value[idx]);
  };

  const hasPaging = (table) => {
    return table.ui && table.ui.allow_paging;
  };

  const getPagedData = (table) => {
    if (!hasPaging(table)) return table.tableData;

    const state = paginationState[table.id];
    if (!state) return table.tableData;

    const start = (state.page - 1) * state.pageSize;
    const end = start + state.pageSize;
    return table.tableData.slice(start, end);
  };

  const handlePageChange = (table, page) => {
    if (paginationState[table.id]) {
      paginationState[table.id].page = page;
    }
  };

  const handlePageSizeChange = (table, size) => {
    if (paginationState[table.id]) {
      paginationState[table.id].pageSize = size;
      paginationState[table.id].page = 1;
    }
  };

  const fetchPageData = async () => {
    try {
      const pageId = route.params.id;
      const pageRes = await mushroom.dynamic_config_page.findByIdAsync({
        id: pageId,
      });
      if (pageRes.result) {
        const page = pageRes.result;
        pageName.value = getDisplayName(page);

        const nameVi = (page.name_lang && page.name_lang.vi) || page.name || "";
        const nameEn = (page.name_lang && page.name_lang.en) || page.name || "";
        breadcrumbStore.setBreadcrumb({
          vi: [
            { text: "Trang chủ", href: "/" },
            { text: nameVi, href: "" },
          ],
          en: [
            { text: "Home", href: "/" },
            { text: nameEn, href: "" },
          ],
        });

        if (page.table_ids && page.table_ids.length > 0) {
          for (const tableId of page.table_ids) {
            try {
              const tableRes = await mushroom.dynamic_config_table.findByIdAsync({
                id: tableId,
              });
              if (tableRes.result) {
                const tableConfig = tableRes.result;
                const dataRes =
                  await mushroom.dynamic_config_table.get_config_dataAsync({
                    params: { id: tableId },
                  });
                const data = dataRes.result.data || [];
                configTables.value.push({
                  ...tableConfig,
                  tableData: data,
                  originalData: data,
                });

                if (tableConfig.ui && tableConfig.ui.allow_paging) {
                  paginationState[tableId] = { page: 1, pageSize: 10 };
                }
              }
            } catch (e) {
              console.error("Lỗi lấy bảng cấu hình:", tableId, e);
            }
          }
        }
      }
    } catch (e) {
      MethodService.showError(e.code);
    } finally {
      loading.value = false;
    }
  };

  const reloadTableData = async (table) => {
    try {
      const dataRes = await mushroom.dynamic_config_table.get_config_dataAsync({
        params: { id: table.id },
      });
      const tableData = dataRes.result?.data || [];
      const idx = configTables.value.findIndex((t) => t.id === table.id);
      if (idx !== -1) {
        configTables.value[idx].originalData = tableData;
        applyFilterAndSort(configTables.value[idx]);
      }
    } catch (e) {
      console.error("Lỗi reload data:", e);
    }
  };

  const openEditDialog = (table, row) => {
    editingTable.value = table;
    editingRow.value = row;
    editDialogTitle.value = getDisplayName(table);
    editValues.value = table.value_columns.map((vc) => ({
      code: vc.code,
      name: getValueColumnName(vc),
      type: vc.type_mode === "sql" ? row[vc.type_code] : vc.type,
      min_code: vc.min_code,
      max_code: vc.max_code,
      min: vc.min_code && row[vc.min_code] != null ? row[vc.min_code] : null, // dùng để hiển thị ra giao diện
      max: vc.max_code && row[vc.max_code] != null ? row[vc.max_code] : null, // dùng để hiển thị ra giao diện
      value: row[vc.code],
      errors: [],
    }));
    showEditDialog.value = true;
  };

  const validateEditValues = () => {
    let isValid = true;
    console.log('editValues', editValues.value)
    for (const item of editValues.value) {
      item.errors = [];
      if (
        (item.type === "integer" || (item.type && item.type.toLowerCase() === "int")) &&
        item.value != null &&
        /[,.]/.test(String(item.value))
      ) {
        item.errors.push(`${item.name || item.code}: ${t("t-dynamic-config-validate-integer")}`);
        isValid = false;
      }
      if (MethodService.isNumericValue(item.value)) {
        const val = Number(item.value);
        const row = editingRow.value;
        if (item.min_code && row[item.min_code] != null) {
          const minVal = Number(row[item.min_code]);
          if (!isNaN(minVal) && val < minVal) {
            item.errors.push(
              `${item.name || item.code}: ${t("t-dynamic-config-validate-min")} ${minVal}`
            );
            isValid = false;
          }
        }
        if (item.max_code && row[item.max_code] != null) {
          const maxVal = Number(row[item.max_code]);
          if (!isNaN(maxVal) && val > maxVal) {
            item.errors.push(
              `${item.name || item.code}: ${t("t-dynamic-config-validate-max")} ${maxVal}`
            );
            isValid = false;
          }
        }
      }
    }
    return isValid;
  };

  const saveEdit = async () => {
    if (!validateEditValues()) return;

    const table = editingTable.value;
    const rowId = getRowId(table, editingRow.value);
    const values = editValues.value.map((item) => (item.value != null ? String(item.value) : ""));

    try {
      await mushroom.dynamic_config_table.set_configAsync({
        body: {
          dynamic_config_table_id: table.id,
          id: rowId,
          values: values,
        },
      });
      toastr.success(t("t-dynamic-config-save-success"));
      showEditDialog.value = false;
      await reloadTableData(table);
    } catch (e) {
      MethodService.showError(e.code);
    }
  };

  const handleReset = (table, row) => {
    Swal.fire({
      title: t("t_warning"),
      text: t("t-dynamic-config-reset-confirm"),
      icon: "warning",
      confirmButtonColor: "#f46a6a",
      confirmButtonText: t("swal_accept"),
      cancelButtonText: t("swal_cancel"),
      showCancelButton: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const rowId = getRowId(table, row);
        const values = table.value_columns.map((vc) => {
          const defaultVal = vc.default_value_code
            ? row[vc.default_value_code]
            : row[vc.code];
          return defaultVal != null ? String(defaultVal) : "";
        });

        try {
          await mushroom.dynamic_config_table.set_configAsync({
            body: {
              dynamic_config_table_id: table.id,
              id: rowId,
              values: values,
            },
          });
          toastr.success(t("t-dynamic-config-reset-success"));
          await reloadTableData(table);
        } catch (e) {
          MethodService.showError(e.code);
        }
      }
    });
  };

  const resetState = () => {
    loading.value = true;
    configTables.value = [];
    pageName.value = "";
  };

  const clearBreadcrumb = () => {
    breadcrumbStore.clearBreadcrumb();
  };

  return {
    loading,
    pageName,
    configTables,
    showEditDialog,
    editingRow,
    editingTable,
    editValues,
    editDialogTitle,
    collapsedTables,
    sortState,
    searchKeyword,
    paginationState,

    getDisplayName,
    getColumnLabel,
    getIconUrl,
    hasDefaultValue,
    hasSearchableColumns,
    hasPaging,
    getPagedData,

    toggleCollapse,
    handleSort,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    fetchPageData,
    openEditDialog,
    saveEdit,
    handleReset,
    resetState,
    clearBreadcrumb,
  };
}
